<template>
  <div class="tax-report">
    <LoadingSpinner v-if="fetchingData" style="z-index: 10000" />
    <TitleBar>
      <template v-slot:title>
        <span>{{ pageTitle }}</span>
        <GekkoButton
          icon="fa fa-question-circle"
          variant="link"
          class="text-blue"
          v-b-tooltip.click="
            $gettext(
              'The VAT report helper will guide you through filling out a standard dutch tax returns form for your quarterly VAT report. The report for each quarter is based on the data you already entered through using Gekko to send your invoices and track your costs.'
            )
          "
        />
      </template>
      <template v-slot:subtitle>
        <div class="subtitle-date-calculated">
          <translate>Calculated on:</translate>&nbsp;
          <span v-if="report.modified != null">{{ report.modified | formatDate }}</span>
        </div>
      </template>
      <template v-slot:buttons>
        <div style="display: flex; gap: 0.5rem; flex-wrap: wrap">
          <GekkoButton :text="calculatedStatus" type="button" variant="outline-primary" @click="calculateModal" />
          <GekkoButton
            v-if="report.id"
            :text="$gettext('Download PDF')"
            type="button"
            @click="downloadPDF(report.id)"
            variant="outline-primary"
          />
          <GekkoButton :text="$gettext('All VAT reports')" type="button" :to="upOneLevel" />
        </div>
      </template>
    </TitleBar>
    <div class="content">
      <div v-if="report.quarter === 'Q4'" style="white-space: nowrap; display: inline-block; padding-bottom: 0.5rem;">
        <b-form-checkbox
          v-model="report.q4_show_correction"
          id="checkbox-q4-correction"
          style="display: inline-block; font-size: 16px"
          name="checkbox-q4-correction"
          @change="updateQ4ShowCorrection"
          class="text-blue"
        >
          {{ $gettext('include corrections from previous quarters') }}
        </b-form-checkbox>
        <GekkoButton
          icon="fa fa-question-circle"
          variant="link"
          class="text-blue"
          v-b-tooltip.click="
            $gettext(
              'If checked, any difference between your current Q1, Q2 and Q3 data and the one used to generate their past VAT reports will be shown and added to the Q4 results as a correction'
            )
          "
        />
      </div>
      <b-modal id="modal-multi-recalculate" size="lg" title="Recalculate report" @ok="showSurvey = true" no-stacking>
        <h4>VAT report has already been calculated for this period, are you sure you want to overwrite it?</h4>
      </b-modal>
      <SurveyModal v-if="showSurvey" @close="closeSurveyModal" @submit="handleSubmit" ref="survey" />
      <div class="tax-report-table-wrap">
        <table>
          <tbody>
            <tr class="col-header">
              <th colspan="2">Prestaties binnenland</th>
              <th class="numbers">
                <div>
                  <span v-if="report.quarter === 'Q4' && report.q4_show_correction">
                    <GekkoButton
                      icon="fa fa-question-circle"
                      variant="link"
                      class="text-blue"
                      v-b-tooltip.click="
                        $gettext(
                          'The amounts in brackets are the corrections from previous quarters that have been added to the Q4 results'
                        )
                      "
                    />
                    &nbsp;
                    <small><translate>(incl. corrections)</translate></small>
                  </span>
                  &nbsp;
                  <translate>Total</translate>
                </div>
              </th>
              <th class="numbers">
                <div>
                  <span v-if="report.quarter === 'Q4' && report.q4_show_correction">
                    <GekkoButton
                      icon="fa fa-question-circle"
                      variant="link"
                      class="text-blue"
                      v-b-tooltip.click="
                        $gettext(
                          'The amounts in brackets are the corrections from previous quarters that have been added to the Q4 results'
                        )
                      "
                    />
                    &nbsp;
                    <small><translate>(incl. corrections)</translate></small>
                  </span>
                  &nbsp;
                  <translate>VAT</translate>
                </div>
              </th>
            </tr>
            <tr>
              <td class="row-id">1a</td>
              <td>
                Leveringen/diensten belast met hoog %
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="$gettext('In 1a we add up all invoice line items with 21% VAT.')"
                />
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.invoices_high_net | currency('EUR', 0) }}
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.invoices_high_vat | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].invoices_high_net < 0,
                    'text-invoiced': report.q4_children[0].invoices_high_net > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].invoices_high_net | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].invoices_high_net | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].invoices_high_vat < 0,
                    'text-invoiced': report.q4_children[0].invoices_high_vat > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].invoices_high_vat | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].invoices_high_vat | currency('EUR', 0) }}
              </td>
            </tr>
            <tr>
              <td class="row-id">1b</td>
              <td>
                Leveringen/diensten belast met laag %
                <GekkoButton
                  v-if="report.year < 2019"
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="$gettext('In 1b we add up all invoice line items with 6% VAT.')"
                />
                <GekkoButton
                  v-if="report.year > 2018"
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="$gettext('In 1b we add up all invoice line items with 9% VAT.')"
                />
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.invoices_low_net | currency('EUR', 0) }}
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.invoices_low_vat | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].invoices_low_net < 0,
                    'text-invoiced': report.q4_children[0].invoices_low_net > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].invoices_low_net | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].invoices_low_net | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].invoices_low_vat < 0,
                    'text-invoiced': report.q4_children[0].invoices_low_vat > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].invoices_low_vat | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].invoices_low_vat | currency('EUR', 0) }}
              </td>
            </tr>
            <tr>
              <td class="row-id">1c</td>
              <td>
                Leveringen/diensten belast met overige tarieven, behalve 0%
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'In 1c we automatically add up any invoice lines that do not have 21%, 6% or 0% VAT. Do note that in the Netherlands you can only deviate from these VAT % in specific cases. Please contact your tax advisor if you want to know more about this.'
                    )
                  "
                />
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.invoices_other_net | currency('EUR', 0) }}
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.invoices_other_vat | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].invoices_other_net < 0,
                    'text-invoiced': report.q4_children[0].invoices_other_net > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].invoices_other_net | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].invoices_other_net | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].invoices_other_vat < 0,
                    'text-invoiced': report.q4_children[0].invoices_other_vat > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].invoices_other_vat | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].invoices_other_vat | currency('EUR', 0) }}
              </td>
            </tr>
            <tr>
              <td class="row-id">1d</td>
              <td>
                Privégebruik
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'Here you can adjust your VAT amount to reflect private use of company assets. Gekko only allows you to report this at the end of the year when you fully know what happened. You can report this either in Q4 or December when you finalize for the fiscal year.'
                    )
                  "
                />
              </td>
              <td v-if="report.id && !report.q4_show_correction" class="numbers">
                {{ (report.privegebruik_vat / 0.21).toFixed(2) | currency('EUR', 0) }}
              </td>
              <td v-if="report.id && !report.q4_show_correction" class="numbers">
                {{ report.privegebruik_vat | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].privegebruik_vat / 0.21 < 0,
                    'text-invoiced': report.q4_children[0].privegebruik_vat / 0.21 > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ (report.q4_children[0].privegebruik_vat / 0.21) | currency('EUR', 0) }})</small
                >
                {{ (report.q4_children[1].privegebruik_vat / 0.21) | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].privegebruik_vat < 0,
                    'text-invoiced': report.q4_children[0].privegebruik_vat > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].privegebruik_vat | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].privegebruik_vat | currency('EUR', 0) }}
              </td>
            </tr>
            <tr>
              <td class="row-id">1e</td>
              <td>
                Leveringen/diensten belast met 0% of niet bij u belast
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'In 1e we add up all invoices lines with either 0% or with a reversed charge to the customers.'
                    )
                  "
                />
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.invoices_zero_net | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].invoices_zero_net < 0,
                    'text-invoiced': report.q4_children[0].invoices_zero_net > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].invoices_zero_net | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].invoices_zero_net | currency('EUR', 0) }}
              </td>
              <td>&nbsp;</td>
            </tr>
            <tr class="col-header">
              <th colspan="4">Verleggingsregelingen binnenland</th>
            </tr>
            <tr>
              <td class="row-id">2a</td>
              <td>
                Leveringen/diensten waarbij de omzetbelasting naar u is verlegd
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'In 2a we add up all costs with the checkbox `reversed charge` checked. This means that your supplier did not yet pay VAT and you should pay VAT on these goods. Note: the net effect will be 0 as we will deduct VAT on reversed charges as pretax under 5b. Since 2a only applies to goods and services delivered from within the Netherlands, we ask you to indicate how much of reversed charge costs where shipped from abroad, and we fill out the results in 4a and 4b.'
                    )
                  "
                />
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.incoming_reversed_charge | currency('EUR', 0) }}
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.incoming_reversed_charge_vat | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].incoming_reversed_charge < 0,
                    'text-invoiced': report.q4_children[0].incoming_reversed_charge > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].incoming_reversed_charge | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].incoming_reversed_charge | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].incoming_reversed_charge_vat < 0,
                    'text-invoiced': report.q4_children[0].incoming_reversed_charge_vat > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].incoming_reversed_charge_vat | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].incoming_reversed_charge_vat | currency('EUR', 0) }}
              </td>
            </tr>
            <tr class="col-header">
              <th colspan="4">Prestaties naar of in het buitenland</th>
            </tr>
            <tr>
              <td class="row-id">3a</td>
              <td>
                Leveringen naar landen buiten de EU (uitvoer)
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'In 3a we add up any invoices you`ve sent to customers outside of the E.U. NOTE: invoicing outside the Netherlands is bound by rules and usually you do not calculate normal VAT. Please consult your tax adviser if you want to know more about this.'
                    )
                  "
                />
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.export_outside_eu | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].export_outside_eu < 0,
                    'text-invoiced': report.q4_children[0].export_outside_eu > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].export_outside_eu | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].export_outside_eu | currency('EUR', 0) }}
              </td>
              <td>&nbsp;</td>
            </tr>
            <tr>
              <td class="row-id">3b</td>
              <td>
                Leveringen naar of diensten in landen binnen de EU
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'In 3b we add up any invoices you`ve sent to customers outside the Netherlands but within the E.U. NOTE: invoicing outside the Netherlands is bound by rules and usually you do not calculate normal VAT. Please consult your tax adviser if you want to know more about this.'
                    )
                  "
                />
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">{{ report.export_to_eu | currency('EUR', 0) }}</td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].export_to_eu < 0,
                    'text-invoiced': report.q4_children[0].export_to_eu > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].export_to_eu | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].export_to_eu | currency('EUR', 0) }}
              </td>
              <td>&nbsp;</td>
            </tr>
            <tr>
              <td class="row-id unsupported">3c</td>
              <td class="unsupported">
                Installatie/afstandsverkopen binnen de EU
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'In 3c you should fill out any installation services of goods to other E.U. countries or any goods delivered to customers without a VAT identification number. Since this is fairly complex and applies only to very few small entrepreneurs, Gekko does not support the calculation for row 3c. If you do feel 3c applies to your business, we advice you to talk to your tax advisor.'
                    )
                  "
                />
              </td>
              <td v-if="report" class="unsupported numbers">{{ 0 | currency('EUR', 0) }}</td>
              <td>&nbsp;</td>
            </tr>
            <tr class="col-header">
              <th colspan="4">Prestaties vanuit het buitenland aan u verricht</th>
            </tr>
            <tr>
              <td class="row-id">4a</td>
              <td>
                Leveringen/diensten uit landen buiten de EU
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'Goods and services that you purchase from other countries are usually taxed in the Netherlands. If you purchase goods from other countries, the supplier often charges you no VAT. If this happens you still need to pay VAT over your purchase in the Netherlands. You can do this by by selecting &quot;VAT reverse charge&quot; when you enter the purchase. If you do that and indicate that the purchase was from abroad, the VAT will be declared under 4a and 4b. NOTE: the same amount will be deducted in 5b as well. So you do not actually pay any more VAT.'
                    )
                  "
                />
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.import_outside_eu | currency('EUR', 0) }}
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.import_outside_eu_vat | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].import_outside_eu < 0,
                    'text-invoiced': report.q4_children[0].import_outside_eu > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].import_outside_eu | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].import_outside_eu | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].import_outside_eu_vat < 0,
                    'text-invoiced': report.q4_children[0].import_outside_eu_vat > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].import_outside_eu_vat | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].import_outside_eu_vat | currency('EUR', 0) }}
              </td>
            </tr>
            <tr>
              <td class="row-id">4b</td>
              <td>
                Leveringen/diensten uit landen binnen de EU
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'Goods and services that you purchase from other countries are usually taxed in the Netherlands. If you purchase goods from other countries, the supplier often charges you no VAT. If this happens you still need to pay VAT over your purchase in the Netherlands. You can do this by by selecting &quot;VAT reverse charge&quot; when you enter the purchase. If you do that and indicate that the purchase was from abroad, the VAT will be declared under 4a and 4b. NOTE: the same amount will be deducted in 5b as well. So you do not actually pay any more VAT.'
                    )
                  "
                />
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.import_inside_eu | currency('EUR', 0) }}
              </td>
              <td v-if="!report.q4_show_correction" class="numbers">
                {{ report.import_inside_eu_vat | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].import_inside_eu < 0,
                    'text-invoiced': report.q4_children[0].import_inside_eu > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].import_inside_eu | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].import_inside_eu | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].import_inside_eu_vat < 0,
                    'text-invoiced': report.q4_children[0].import_inside_eu_vat > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].import_inside_eu_vat | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].import_inside_eu_vat | currency('EUR', 0) }}
              </td>
            </tr>
            <tr class="col-header">
              <th colspan="4">Voorbelasting, kleine ondernemersregeling, schatting en eindtotaal</th>
            </tr>
            <tr>
              <td class="row-id">5a</td>
              <td>Verschuldigde omzetbelasting</td>
              <td>&nbsp;</td>
              <td v-if="!report.q4_show_correction" class="numbers">{{ report.vat_owed | currency('EUR', 0) }}</td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].vat_owed < 0,
                    'text-invoiced': report.q4_children[0].vat_owed > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].vat_owed | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].vat_owed | currency('EUR', 0) }}
              </td>
            </tr>
            <tr>
              <td class="row-id">5b</td>
              <td>
                Voorbelasting
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'In 5b we add up all the VAT under the `costs` tab. Do note that we treat different cost items differently. For instance, VAT on food and beverages (catered) and  personal costs will not be subtracted. And VAT on Representational gifts is capped at a certain amount. Also, any customized cost item which has VAT deductible disabled, will not be subtracted.'
                    )
                  "
                />
              </td>
              <td>&nbsp;</td>
              <td v-if="!report.q4_show_correction" class="numbers">{{ report.vat_paid | currency('EUR', 0) }}</td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].vat_paid < 0,
                    'text-invoiced': report.q4_children[0].vat_paid > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].vat_paid | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].vat_paid | currency('EUR', 0) }}
              </td>
            </tr>
            <tr>
              <td class="row-id">5c</td>
              <td>Subtotaal</td>
              <td>&nbsp;</td>
              <td v-if="!report.q4_show_correction" class="numbers">{{ report.subtotal | currency('EUR', 0) }}</td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].subtotal < 0,
                    'text-invoiced': report.q4_children[0].subtotal > 0,
                  }"
                  >( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].subtotal | currency('EUR', 0) }})</small
                >
                {{ report.q4_children[1].subtotal | currency('EUR', 0) }}
              </td>
            </tr>
            <tr v-if="!report.year < 2020">
              <td class="row-id unsupported">5d</td>
              <td class="unsupported">
                Vermindering volgens de kleine ondernemersregeling
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'The kleine ondernemersregeling was a rule in the vat returns until december 2019. Starting 2020, this rule has changed and 5d is no longer applicable to any company. This question has therefore been grayed out until it is clear how the Belastingdienst has removed this item from the vat returns'
                    )
                  "
                />
              </td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
            </tr>
            <tr v-if="report.year < 2020">
              <td class="row-id">5d</td>
              <td>
                Vermindering volgens de kleine ondernemersregeling
                <GekkoButton
                  icon="fa fa-question-circle"
                  variant="link"
                  class="text-blue"
                  v-b-tooltip.click="
                    $gettext(
                      'In 5d we calculate the kleine ondernemingsregeling. The kleine ondernemingsregeling is a rule applicable to small companies that do not expect to pay more than €1883 a year. These companies will receive a discount on their VAT payment. Gekko will automatically calculate how much this discount should be if you indicate that we should apply this rule. <br />This rule only applies to companies with a eenmanszaak, VoF or Maatschap structure. If you want to know more about this rule and whether it applies to your company, please contact your tax advisor.'
                    )
                  "
                />
              </td>
              <td>&nbsp;</td>
              <td v-if="!report.q4_show_correction && report.quarter !== 'Q4'" class="numbers">
                {{ report.small_business_discount_total | currency('EUR', 0) }}
              </td>
              <td v-if="!report.q4_show_correction && report.quarter === 'Q4'" class="numbers">
                {{ report.small_business_discount_total_q4 | currency('EUR', 0) }}
              </td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].small_business_discount_total_q4 < 0,
                    'text-invoiced': report.q4_children[0].small_business_discount_total_q4 > 0,
                  }"
                >
                  ( <font-awesome-icon icon="fa fa-arrow-up" />
                  {{ report.q4_children[0].small_business_discount_total_q4 | currency('EUR', 0) }})
                </small>
                {{ report.small_business_discount_total_q4 | currency('EUR', 0) }}
              </td>
            </tr>
            <tr>
              <td class="row-id unsupported">5e</td>
              <td class="unsupported">Schatting vorige aangifte(n)</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
            </tr>
            <tr>
              <td class="row-id">5f</td>
              <td>Schatting deze aangifte</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
            </tr>
            <tr>
              <td class="row-id">5g</td>
              <td>Totaal te betalen / terug te vragen</td>
              <td>&nbsp;</td>
              <td v-if="!report.q4_show_correction" class="numbers">{{ report.total | currency('EUR', 0) }}</td>
              <td v-if="report.quarter === 'Q4' && report.q4_show_correction" class="numbers">
                <small
                  class="correction"
                  :class="{
                    'text-danger': report.q4_children[0].total < 0,
                    'text-invoiced': report.q4_children[0].total > 0,
                  }"
                >
                  ( <font-awesome-icon icon="fa fa-arrow-up" /> {{ report.q4_children[0].total | currency('EUR', 0) }})
                </small>
                {{ report.q4_children[1].total | currency('EUR', 0) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'

import { promptDownload } from '@/utils/'

import SurveyModal from '@/components/ui/SurveyModal'
import settingsPageSetup from '@/mixins/settingsPageSetup'
import GekkoButton from '@/components/GekkoButton'
import ReportsService from '@/store/services/reports.service'
import TitleBar from '@/components/TitleBar'
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'

export default {
  name: 'Taxreport',
  mixins: [settingsPageSetup],
  components: { TitleBar, GekkoButton, SurveyModal, LoadingSpinner },
  data() {
    return {
      pageSettings: {
        title: this.$gettext('Create report'),
        showCompanyPicker: false,
      },
      MONTHS: [
        { text: this.$gettext('Jan'), value: 1 },
        { text: this.$gettext('Feb'), value: 2 },
        { text: this.$gettext('Mar'), value: 3 },
        { text: this.$gettext('Apr'), value: 4 },
        { text: this.$gettext('May'), value: 5 },
        { text: this.$gettext('Jun'), value: 6 },
        { text: this.$gettext('Jul'), value: 7 },
        { text: this.$gettext('Aug'), value: 8 },
        { text: this.$gettext('Sep'), value: 9 },
        { text: this.$gettext('Oct'), value: 10 },
        { text: this.$gettext('Nov'), value: 11 },
        { text: this.$gettext('Dec'), value: 12 },
      ],
      report: [],
      reportsToOverwrite: [],
      showSurvey: false,
      startDate: '',
      endDate: '',
      isPremium: false,
      fetchingData: false,
      checkInterval: null,
    }
  },
  mounted() {
    this.isPremium = this.$store.getters['getIsPremium']
    if (this.isCreateView) {
      if (Object.keys(this.reportInitialValues).length === 0) {
        this.$router.push(this.upOneLevel)
        return
      }
      if (this.isPremium || this.currentUser.is_accountant) {
        this.showSurvey = true
      } else {
        this.$store.commit('togglePremiumModal', 'web_reports_vat', { root: true })
      }
    } else {
      this.fetchingData = true
      ReportsService.get(this.$route.params.id, { extended: true }).then((response) => {
        this.fetchingData = false
        this.report = response.data
        let report_initial_values = {
          company: this.currentCompany.id,
          year: this.report.year,
          month: this.report.month,
          quarter: this.report.month ? this.calculateQuarter(this.report.month) : this.report.quarter,
          consolidated: this.report.consolidated,
        }
        if(this.report.waiting_task){
          this.checkReportStatus();
        }
        this.$store.commit('taxreports/setReportInitialValues', report_initial_values)
      })
    }
  },
  beforeDestroy() {
    clearInterval(this.checkInterval)
  },
  computed: {
    isCreateView: function () {
      return this.$route.path.search('vat/create') > -1
    },
    calculatedStatus: function () {
      if (this.isCreateView) return this.$gettext('Calculate')
      return this.$gettext('Recalculate')
    },
    pageTitle: function () {
      var title =
        this.reportInitialValues.month != null
          ? this.getMonth(this.reportInitialValues.month, this.MONTHS)
          : this.reportInitialValues.quarter
      title += ' ' + this.reportInitialValues.year
      return this.$gettext('Sales tax report for %period%').replace('%period%', title)
    },
    ...mapGetters({
      reportInitialValues: 'taxreports/getReportInitialValues',
      currentCompany: 'main/getCurrentCompany',
      currentUser: 'main/getCurrentUser',
      getCompanyId: 'main/getCompanyId'
    }),
    upOneLevel() {
      if (this.$route.path.includes('control-panel'))
        return { name: 'ControlPanelVAT', params: { customerId: this.getCompanyId } }
      else return { name: 'TaxreportList' }
    },
  },
  filters: {
    formatDate: function (value) {
      var options = { year: 'numeric', month: 'long', day: 'numeric' }
      return new Date(value).toLocaleDateString('en-US', options)
    },
  },
  methods: {
    closeSurveyModal() {
      this.showSurvey = false
      document.body.classList.remove('modal-on')
    },
    checkReportStatus(){
      this.fetchingData = true
      this.checkInterval = setInterval(() => {
        ReportsService.status().then((response)=>{
          if(!response.data.includes(this.report.id)){
            ReportsService.get(this.$route.params.id, { extended: true }).then((response) => {
              this.fetchingData = false
              this.report = response.data
            })
            clearInterval(this.checkInterval)
          }
        })
      }, 5 * 1000);
    },
    calculateModal() {
      if (this.isPremium || this.currentUser.is_accountant) {
        this.showSurvey = true
      } else {
        this.$store.commit('togglePremiumModal', 'web_reports_vat', { root: true })
      }
    },
    calculateQuarter(month) {
      return 'Q' + Math.floor((month + 2) / 3)
    },
    getMonth(value, months) {
      const result = months.filter((month) => month.value === value)
      return result[0].text
    },
    updateQ4ShowCorrection() {
      ReportsService.updateQ4ShowCorrectionTrigger(this.report.id, { show: this.report.q4_show_correction })
    },
    handleSubmit(answers) {
      var data = {
        over_gift_limit: answers.find((obj) => obj.id === 'over_gift_limit')?.value || false,
        import_without_vat: answers.find((obj) => obj.id === 'import_without_vat')?.value || false,
        treat_non_dutch_as_dutch: answers.find((obj) => obj.id === 'treat_non_dutch_as_dutch')?.value || false,
        small_business_discount: false,
      }

      if (data['import_without_vat'] === true || data['import_without_vat'] === 'true') {
        data['import_inside_eu'] = parseInt(answers.find((obj) => obj.id === 'import_inside_eu').value)
        data['import_outside_eu'] = parseInt(answers.find((obj) => obj.id === 'import_outside_eu').value)
      }

      if (data['over_gift_limit'] === true || data['over_gift_limit'] === 'true') {
        data['gift_paid'] = answers.find((obj) => obj.id === 'gift_paid').value
      }

      if (
        answers.find((obj) => obj.id === 'private_car_costs') &&
        answers.find((obj) => obj.id === 'private_car_costs').value === 'true'
      ) {
        data['car_net'] = parseInt(answers.find((obj) => obj.id === 'car_net').value)
        data['car_vat'] = parseInt(answers.find((obj) => obj.id === 'car_vat').value)
        data['car_used_privately'] = true
      }

      if (
        answers.find((obj) => obj.id === 'private_expenses') &&
        answers.find((obj) => obj.id === 'private_expenses').value === 'true'
      ) {
        data['private_expenses_net'] = parseInt(answers.find((obj) => obj.id === 'private_expenses_net').value)
        data['private_expenses_vat'] = parseInt(answers.find((obj) => obj.id === 'private_expenses_vat').value)
      }

      if (this.isCreateView) {
        if (this.$route.name === "ControlPanelCreateVAT") {
          data['company'] = this.getCompanyId
        } else {
          data['company'] = this.reportInitialValues.company
        }
        
        data['year'] = this.reportInitialValues.year
        data['month'] = this.reportInitialValues.month
        data['quarter'] = this.reportInitialValues.quarter
        data['consolidated'] = this.reportInitialValues.consolidated

        this.fetchingData = true
        ReportsService.create(data).then((response) => {
          this.fetchingData = false
          this.report = response.data
          this.closeSurveyModal()
          if (this.$route.path.includes('control-panel'))
            this.$router.push({
              name: 'ControlPanelViewVAT',
              params: { customerId: this.getCompanyId, id: response.data.id },
            })
          else this.$router.push({ name: 'ViewTaxreport', params: { id: response.data.id } })
        })
      } else {
        this.fetchingData = true
        ReportsService.update(this.report.id, data, { extended: true }).then((response) => {
          this.fetchingData = false
          this.report = response.data
          if(this.report.waiting_task){
            this.$store.commit('setAlert', {
              title: this.$gettext("Scheduled"),
              text: this.$gettext("The report has been scheduled. Check back later to see it."),
              variant: 'success',
              duration: 4
            });
            this.checkReportStatus();
          }
          this.closeSurveyModal()
        })
      }
    },
    downloadPDF(report_id) {
      ReportsService.getPDFBlob(report_id)
        .then((response) => {
          promptDownload(response)
        })
        .catch((error) => console.error('Error downloading PDF:', error))
    },
  },
}
</script>
<style lang="scss">
.tax-report {
  .pagetitle--main {
    h2 {
      font-size: 24px !important;
      @media (min-width: '576px') {
        font-size: 28px;
      }
    }
  }
  .subtitle-date-calculated {
    color: $colorAppHours;
    font-size: 1rem;
  }
  .content {
    padding: 0 0.5rem;
    @media (min-width: '576px') {
      padding: 0 1rem;
    }
  }
  .numbers {
    width: 25%;
    text-align: right;
  }
  .unsupported {
    color: $colorTextLight;
  }
  .correction {
    display: none;
    &.text-invoiced,
    &.text-danger {
      display: inline-block;
    }
  }
  .tax-report-table-wrap {
    width: 100%;
    overflow: scroll;
    @media (min-width: '576px') {
      overflow: visible;
    }
  }
  table {
    width: 100%;
    border: 1px solid $tableBorderColor;
    .text-blue.btn-link.icon-only {
      padding: 0;
      padding-left: 0.5rem;
    }
    .col-header {
      th {
        background-color: $tableHeadColorBg;
        padding: 0.5rem;
        &:not(.numbers) {
          text-transform: uppercase;
          font-family: DINNextLTPro-Medium, sans-serif;
          font-size: 1rem;
        }
      }
    }
    td {
      padding: 0.5rem;
      font-size: 14px;
    }
    td.row-id {
      width: 40px;
      transition: background-color 0.1s ease;
      background: $colorTableOddBg;
      border-right: 1px solid $tableBorderColor;
      font-size: 16px;
    }
    tbody {
      tr {
        border-bottom: 1px solid $tableBorderColor;
        transition: background-color 0.1s ease;
      }
    }
    @media (min-width: '576px') {
      .text-blue.btn-link.icon-only {
        display: inline-block;
      }
      .col-header {
        th {
          padding: 1rem;
          &:not(.numbers) {
            font-size: 18px;
          }
        }
      }
      td {
        font-size: 1rem;
        padding: 0;
      }
      td.row-id {
        width: 45px;
        font-size: 18px;
      }
      tbody {
        tr {
          td {
            padding: 1rem 0.75rem;
          }
          &:hover {
            background-color: $colorTableHoverBg;
            .row-id {
              background-color: $colorTableHoverBg;
            }
          }
        }
      }
    }
  }
}
</style>